import React, { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Button, Form, Select, Table, Spin, Typography } from 'antd'
import { format } from 'date-fns'
import { getReportByProject } from '../../../services/application'
import ProjectSelector from '../../ProjectSelector'
import { APP_STATUS_AGREE, APP_STATUS_DELIVERY, APP_STATUS_DONE, APP_STATUS_PAY, statuses } from '../constants'
import { exportTableDataToXlsx } from '../../../utils/export-xls'
import { userSelector } from '../../../store/session'
import { getUserProjectNameById } from '../report-utils'
import { formatMoney } from '../../../utils/number-utils'

const { Text } = Typography

const defaultStatuses = [APP_STATUS_AGREE, APP_STATUS_PAY, APP_STATUS_DELIVERY, APP_STATUS_DONE]

const DEFAULT_HEIGHT = window.innerHeight || 600

const getVatType = (vatType) => {
  switch (vatType) {
    case 'none':
      return 'Без НДС'
    case 'up':
      return 'НДС сверху'
    case 'include':
      return 'НДС в сумме'
    default:
      return ''
  }
}

const getPaymentType = (vatType) => {
  switch (vatType) {
    case 'cashless':
      return 'Безналичный'
    case 'cash':
      return 'Наличный'
    case 'postponement':
      return 'Безналичный пост оплата'
    case 'postcashless':
      return 'Безналичный пред оплата'
    default:
      return ''
  }
}

const TransportationReport = () => {
  const [form] = Form.useForm()
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [project, setProject] = useState(null)

  const { user } = useSelector(userSelector)

  const onFormSubmit = (values) => {
    setIsLoading(true)

    const { project, statuses } = values

    setProject(project)

    getReportByProject(project, { statuses })
      .then((response) => {
        const results = response?.data?.data?.attributes?.results
        if (results) {
          const transportations = results.reduce((acc, item) => {
            if (item && item?.transportation) {
              item?.transportation.forEach((trans) => {
                acc.push({ applicationId: item.id, ...trans })
              })
            }

            return acc
          }, [])
          setData(transportations)
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }

  const exportData = useCallback(() => {
    let projectName = getUserProjectNameById(user, project)
    exportTableDataToXlsx('div.export-table-transportation', projectName, 'Отчет-Транспорт.xlsx')
  }, [user, project])

  const columns = [
    {
      title: 'Отправлено из',
      dataIndex: 'from',
      key: 'from',
    },
    {
      title: 'Отправлено в',
      dataIndex: 'to',
      key: 'to',
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => {
        if (record.vatType === 'include' && record.vatValue > 0 && price > 0) {
          const vat = (price * record.vatValue) / (100 + record.vatValue)
          return formatMoney(price - vat)
        }
        return formatMoney(price)
      },
    },
    {
      title: 'Тип НДС',
      dataIndex: 'vatType',
      key: 'vatType',
      render: (vatType) => getVatType(vatType),
    },
    {
      title: '% НДС',
      dataIndex: 'vatValue',
      key: 'vatValue',
      render: (vatValue, record) => (vatValue ? `${vatValue} %` : ''),
    },
    {
      title: 'НДС',
      dataIndex: 'vatValue',
      key: 'vatCalc',
      render: (vatValue, record) => {
        if (record.vatType === 'up' && vatValue > 0 && record.price > 0) {
          const vat = (record.price / 100) * vatValue
          return formatMoney(vat)
        } else if (record.vatType === 'include' && vatValue > 0 && record.price > 0) {
          const vat = (record.price * vatValue) / (100 + vatValue)
          return formatMoney(vat)
        }
        return ''
      },
    },
    {
      title: 'Всего с НДС',
      dataIndex: 'vatValue',
      key: 'vatSum',
      render: (vatValue, record) => {
        if (record.vatType === 'up' && vatValue > 0 && record.price > 0) {
          const vat = (record.price / 100) * vatValue
          return formatMoney(record.price + vat)
        }
        return formatMoney(record.price)
      },
    },
    {
      title: 'Оплата',
      dataIndex: 'paymentType',
      key: 'paymentType',
      filters: [
        {
          text: 'Безналичный',
          value: 'cashless',
        },
        {
          text: 'Наличный',
          value: 'cash',
        },
        {
          text: 'Безналичный пост оплата',
          value: 'postponement',
        },
        {
          text: 'Безналичный пред оплата',
          value: 'postcashless',
        },
      ],
      onFilter: (value, record) => record.paymentType === value,
      filterSearch: true,
      render: (paymentType) => getPaymentType(paymentType),
    },
    {
      title: 'Заявка',
      dataIndex: 'applicationId',
      key: 'applicationId',
      render: (applicationId) => (
        <a href={`/applications?state=all&id=${applicationId}`} target="_blank" rel="noreferrer">
          {applicationId}
        </a>
      ),
    },
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => format(new Date(createdAt), 'dd-MM-yy'),
    },
  ]

  return (
    <Spin spinning={isLoading}>
      <div style={{ paddingTop: 10 }}>
        <Form
          initialValues={{ statuses: defaultStatuses }}
          form={form}
          layout="vertical"
          style={{ display: 'flex' }}
          onFinish={onFormSubmit}
        >
          <Form.Item
            style={{ width: 300, maxWidth: 300 }}
            rules={[{ required: true, message: 'Выберите проект' }]}
            label="Проект"
            name="project"
          >
            <ProjectSelector />
          </Form.Item>
          <Form.Item style={{ marginLeft: 15 }} label="Статусы заявки" name="statuses">
            <Select mode="multiple" allowClear options={statuses} dropdownMatchSelectWidth={false} />
          </Form.Item>
          <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
            <Button onClick={() => form.submit()}>Сформировать отчет</Button>
          </Form.Item>
          <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
            <Button disabled={data.length === 0} onClick={exportData}>
              Скачать
            </Button>
          </Form.Item>
        </Form>
        <Table
          columns={columns}
          dataSource={data}
          bordered
          size="small"
          pagination={false}
          scroll={{ y: DEFAULT_HEIGHT - 240 }}
          className="export-table-transportation"
          summary={(pageData) => {
            const sum = pageData.reduce((acc, item) => {
              if (item.vatType === 'include' && item.vatValue > 0 && item.price > 0) {
                const vat = (item.price * item.vatValue) / (100 + item.vatValue)
                return acc + (item.price - vat)
              }
              if (item.price) {
                return acc + item.price
              }
              return acc
            }, 0)

            const vatSum = pageData.reduce((acc, item) => {
              if (item.vatType === 'up' && item.vatValue > 0 && item.price > 0) {
                const vat = (item.price / 100) * item.vatValue
                return item.price + vat + acc
              } else if (item.price > 0) {
                return acc + item.price
              }
              return acc
            }, 0)

            return (
              <Table.Summary>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Итого:</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} colSpan={1}></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <Text strong>{formatMoney(sum)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} colSpan={3}></Table.Summary.Cell>
                  <Table.Summary.Cell index={7}>
                    <Text strong>{formatMoney(vatSum)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )
          }}
        />
      </div>
    </Spin>
  )
}

export default TransportationReport
